/**
 * @module AdapterList
 * @package @geenius/adapters/react-native
 * @description Renders the searchable native adapter list with FlatList.
 */

import type { AdapterDomain, AdapterStatusType } from "@geenius/adapters";
import { ADAPTER_DOMAINS, DOMAIN_LABELS } from "@geenius/adapters";
import type { JSX } from "react";
import { useMemo, useState } from "react";
import type { StyleProp, ViewStyle } from "react-native";
import { semanticColors } from "../bridges/TokenBridge";
import { useAdapters } from "../hooks";
import {
  FlatList,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "../lib/native";
import { AdapterCard } from "./AdapterCard";

/**
 * Props for the searchable native adapter list.
 *
 * @property filterStatus Optional lifecycle status used to filter domains.
 * @property onRefresh Optional native pull-to-refresh callback.
 * @property onSelect Optional callback fired when a domain card is pressed.
 * @property refreshing Whether the native pull-to-refresh control is active.
 * @property style Optional native style merged onto the list container.
 * @property testID Optional deterministic selector for tests and Storybook.
 */
export interface AdapterListProps {
  filterStatus?: AdapterStatusType;
  onRefresh?: () => void | Promise<void>;
  onSelect?: (domain: AdapterDomain) => void;
  refreshing?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

/**
 * Renders a searchable native list of adapter domains.
 *
 * @param props List props including optional status filter and select callback.
 * @returns A React Native list with loading, empty, and filtered states.
 * @example
 * ```tsx
 * <AdapterList filterStatus="connected" onSelect={(domain) => open(domain)} />
 * ```
 */
export function AdapterList({
  onSelect,
  filterStatus,
  onRefresh,
  refreshing = false,
  style,
  testID,
}: AdapterListProps): JSX.Element {
  const { statuses, isLoading } = useAdapters();
  const [search, setSearch] = useState("");

  const filteredDomains = useMemo(() => {
    let domains = [...ADAPTER_DOMAINS];

    if (search) {
      const query = search.toLowerCase();
      domains = domains.filter((domain) => {
        const label = DOMAIN_LABELS[domain].toLowerCase();
        const provider = statuses[domain]?.provider?.toLowerCase() ?? "";
        return (
          label.includes(query) ||
          provider.includes(query) ||
          domain.includes(query)
        );
      });
    }

    if (filterStatus) {
      domains = domains.filter(
        (domain) => statuses[domain]?.status === filterStatus,
      );
    }

    return domains;
  }, [search, filterStatus, statuses]);

  if (isLoading) {
    return (
      <View
        accessibilityLabel="Loading adapters"
        accessibilityRole="progressbar"
        style={[styles.stack, style]}
        testID={testID ?? "adapter-list-loading"}
      >
        {[1, 2, 3, 4].map((index) => (
          <View key={index} style={styles.skeletonCard}>
            <View style={styles.skeletonIcon} />
            <View style={styles.skeletonBody}>
              <View style={styles.skeletonTitle} />
              <View style={styles.skeletonLine} />
            </View>
          </View>
        ))}
      </View>
    );
  }

  return (
    <View style={[styles.container, style]} testID={testID ?? "adapter-list"}>
      <TextInput
        accessibilityLabel="Search adapters"
        autoCapitalize="none"
        autoCorrect={false}
        onChangeText={setSearch}
        placeholder="Search adapters..."
        style={styles.search}
        testID="adapter-search"
        value={search}
      />
      <FlatList
        accessibilityLabel="Adapters"
        accessibilityState={{ busy: refreshing }}
        data={filteredDomains}
        keyExtractor={(domain) => domain}
        ListEmptyComponent={
          <View
            accessibilityLabel="Adapter list empty state"
            style={styles.emptyState}
            testID="adapter-list-empty"
          >
            <Text style={styles.emptyText}>
              {search
                ? "No adapters match your search"
                : "No adapters configured"}
            </Text>
            <Text style={styles.emptyHint}>
              {search
                ? "Clear the search to return to the full adapter list."
                : "Check the current filters or configure an adapter from the overview page."}
            </Text>
            {search ? (
              <Pressable
                accessibilityLabel="Clear adapter search"
                accessibilityRole="button"
                onPress={() => setSearch("")}
                style={styles.emptyAction}
                testID="adapter-list-clear-search"
              >
                <Text style={styles.emptyActionText}>Clear search</Text>
              </Pressable>
            ) : null}
          </View>
        }
        onRefresh={onRefresh}
        renderItem={({ item }) => (
          <View style={styles.item}>
            <AdapterCard
              domain={item}
              onPress={onSelect ? () => onSelect(item) : undefined}
              status={statuses[item]}
            />
          </View>
        )}
        refreshing={refreshing}
        testID="adapter-list-results"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 12,
  },
  emptyState: {
    alignItems: "center",
    backgroundColor: semanticColors.surface.bg,
    borderColor: semanticColors.surface.border,
    borderRadius: 10,
    borderWidth: 1,
    padding: 24,
  },
  emptyText: {
    color: semanticColors.surface.textMuted,
    fontSize: 13,
    fontWeight: "600",
  },
  emptyHint: {
    color: semanticColors.surface.textMuted,
    fontSize: 12,
    marginTop: 6,
    textAlign: "center",
  },
  emptyAction: {
    backgroundColor: semanticColors.surface.card,
    borderColor: semanticColors.surface.border,
    borderRadius: 8,
    borderWidth: 1,
    marginTop: 12,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  emptyActionText: {
    color: semanticColors.surface.text,
    fontSize: 13,
    fontWeight: "600",
  },
  item: {
    marginBottom: 10,
  },
  search: {
    backgroundColor: semanticColors.surface.card,
    borderColor: semanticColors.surface.borderStrong,
    borderRadius: 10,
    borderWidth: 1,
    color: semanticColors.surface.text,
    fontSize: 15,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  skeletonBody: {
    flex: 1,
    gap: 10,
  },
  skeletonCard: {
    alignItems: "center",
    backgroundColor: semanticColors.surface.card,
    borderColor: semanticColors.surface.border,
    borderRadius: 10,
    borderWidth: 1,
    flexDirection: "row",
    gap: 14,
    padding: 16,
  },
  skeletonIcon: {
    backgroundColor: semanticColors.surface.skeleton,
    borderRadius: 8,
    height: 44,
    width: 44,
  },
  skeletonLine: {
    backgroundColor: semanticColors.surface.skeleton,
    borderRadius: 999,
    height: 8,
    width: "80%",
  },
  skeletonTitle: {
    backgroundColor: semanticColors.surface.skeletonStrong,
    borderRadius: 999,
    height: 12,
    width: "44%",
  },
  stack: {
    gap: 10,
  },
});
